import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getContestPresentationData, fetchContestMetadata } from '../api/contests';
import { Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Box, useTheme } from '@mui/material';
import { RenderParticipantName } from './Elements';
import { useLoadingCounter } from '../utils/useLoadingCounter';

interface PresentationData {
  participant_id: number;
  participant_name: string;
  result: number;
  grouping: string;
  result_unit: string;
  created_at: string;
  profile_picture: string;
  [key: string]: any; // For dynamic fields
}

const ContestDetailsView: React.FC = () => {
  const { contestId } = useParams<{ contestId: string }>();
  const [presentationData, setPresentationData] = useState<PresentationData[]>([]);
  const [fields, setFields] = useState<any[]>([]);
  const { increment, decrement, isLoading } = useLoadingCounter();
  const theme = useTheme();

  // Determine the main result field if specified in the metadata
  const mainResultField = useMemo(() => {
    const mainField = fields.find((f) => f.main_result_field);
    return mainField ? mainField.field_name : 'result';
  }, [fields]);

  const mainResultLabel = useMemo(() => {
    const mainField = fields.find((f) => f.main_result_field);
    return mainField ? mainField.display_label : 'Результат';
  }, [fields]);

  useEffect(() => {
    const loadData = async () => {
      try {
        increment();
        if (contestId) {
          const presentationData = await getContestPresentationData(parseInt(contestId));
          setPresentationData(presentationData);
        }
      } catch (error) {
        console.error('Error fetching presentation data:', error);
      } finally {
        decrement();
      }

      try {
        increment();
        if (contestId) {
          const metadata = await fetchContestMetadata(contestId);
          setFields(metadata);
        }
      } catch (error) {
        console.error('Error fetching contest metadata:', error);
      } finally {
        decrement();
      }
    };

    loadData();
  }, [contestId]);

  return (
      <div style={{ marginTop: 10 }}>
        <Typography variant="h6" gutterBottom color="primary">
          Лента участников
        </Typography>
        {isLoading ? (
            <Box display="flex" justifyContent="center" mt={2}>
              <CircularProgress />
            </Box>
        ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Участник</TableCell>
                    <TableCell>{mainResultLabel}</TableCell>
                    <TableCell>Группа</TableCell>
                    <TableCell>Выполнен</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {presentationData.map((entry, index) => (
                      <TableRow
                          key={index}
                          style={{
                            backgroundColor:
                                theme.palette.mode === 'dark'
                                    ? index % 2 === 0
                                        ? '#333333'
                                        : '#424242'
                                    : index % 2 === 0
                                        ? '#fafafa'
                                        : 'white', // Alternating row colors based on theme
                          }}
                      >
                        <TableCell>
                          {RenderParticipantName(entry.participant_id, entry.participant_name, entry.profile_picture, true)}
                        </TableCell>
                        <TableCell>{entry[mainResultField]}</TableCell>
                        <TableCell>
                          <Link
                              to={`/contest/${contestId}/?tab=1&group=${encodeURIComponent(entry.grouping)}`}
                              style={{ color: theme.palette.primary.main, textDecoration: 'none' }}
                          >
                            {entry.grouping}
                          </Link>
                        </TableCell>
                        <TableCell>{new Date(entry.created_at).toLocaleDateString('ru-RU')}</TableCell>
                      </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
        )}
      </div>
  );
};

export default ContestDetailsView;
